import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment/moment';
import { BehaviorSubject, distinctUntilChanged, finalize, map, pipe, startWith } from 'rxjs';
import { IApiError } from '@models/iapi';
import { AlertToastService, BreakpointService } from '@services';
import { getError } from '@shared/utils/error.utils';
import { hasFailedEntities } from '@shared/components/partial-success/partial-success.utils';
import { hasSingleError } from '@shared/components/partial-success/partial-success.utils';
import { getStringLength, trimMaxLength, trimSpaces } from '@shared/utils/helpers.util';
import { INewRegisterModalClosePayload } from '@shared/components/registries/new-register/new-register.models';
import { PartialSuccessResponse, RegisterInvoicesInsertPartialSuccessConfig } from '@shared/components/partial-success/partial-success.model';
import { PartialSuccessService } from '@shared/components/partial-success/partial-success.service';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import {
  NewInternationalRegisterModalPayload,
  NewInternationalRegisterMode,
} from './new-international-register.constants';
import { IIntInvoiceRegister, IIntInvoiceRegisterInsertPayload } from '@models/invoice/int-invoice-register';
import { IntRegistersApiService } from 'app/services/int-registers-api/int-registers-api.service';

const descriptionErrors = {
  maxlength: 'Максимальна довжина 30 символів',
  pattern: 'Містить недопустимий символ',
};

@Component({
  selector: 'app-new-international-register',
  templateUrl: './new-international-register.component.html',
  styleUrls: ['./new-international-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewInternationalRegisterComponent extends Unsubscriber implements OnInit {
  protected readonly newRegisterName: string = 'new';
  protected readonly pattern = /^[а-яА-Я,ёЁіІїЇєЄґҐюЮa-zA-Z\d\s\.№;!:/\-'\*\+#`',\/\(\)"«»\\\\_&„""]*$/ui;
  protected readonly descriptionErrors = descriptionErrors;
  protected readonly getError = getError;
  registers: IIntInvoiceRegister[] = [];
  invoicesIds: string[] = [];
  minDate = moment().toDate();
  dateValue = new Date();
  maxLength = 30;
  maxInputLength = 50;
  form: FormGroup;
  closeParams: Record<string, boolean> | null = null;

  isDesktop$ = this.breakpointService.isDesktop$;
  isMobile$ = this.breakpointService.isMobile$;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: NewInternationalRegisterModalPayload,
    public dialogRef: MatDialogRef<NewInternationalRegisterComponent>,
    private intRegistersApi: IntRegistersApiService,
    private alertToastService: AlertToastService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<NativeDateAdapter>,
    private partialSuccess: PartialSuccessService,
    private breakpointService: BreakpointService,
  ) {
    super();
    dateAdapter.setLocale('uk-UA');
  }

  private _pending = new BehaviorSubject(false);

  pending$ = this._pending.asObservable();

  set pending(value: boolean) {
    this._pending.next(value);
  }

  get title(): string {
    return this.data.title ?? 'Додати до реєстру';
  }

  get canViewDescription(): boolean {
    return this.data.canViewDescription ?? true;
  }

  get canViewDate(): boolean {
    return this.data.canViewDate ?? true;
  }

  get description(): AbstractControl {
    return this.form?.get('description') as AbstractControl;
  }

  get registerRef(): AbstractControl {
    return this.form?.get('registerRef') as AbstractControl;
  }

  get date(): AbstractControl {
    return this.form?.get('date') as AbstractControl;
  }

  onDateChange($event): void {
    this.date.setValue(new Date($event));
  }

  onBlur($event: FocusEvent): void {
    let value = ($event.target as HTMLInputElement).value;
    this.checkMaxStringLength(value, true);
  }

  ngOnInit(): void {
    this.initForm();
    this.initRegisters();
    this.initCheckMaxStringLengthSubscription();
  }

  registerDescription(register: IIntInvoiceRegister): string {
    const parts: string[] = [];

    parts.push(register.docNumber || '&mdash;');

    if (!!register.dateTime) {
      parts.push('від ' + moment(register.dateTime ?? '').format('DD.MM.YYYY'));
    }

    if (register.description) {
      parts.push(`(${register.description})`);
    }

    return parts.join(' ');
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.pending = true;

    this.registerRef.value === this.newRegisterName
      ? this.addToNewRegister()
      : this.insertIntoExistRegister();
  }

  clearField(): void {
    this.description?.setValue('');
  }

  private initCheckMaxStringLengthSubscription(): void {
    this.subscriptions = this.description?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value: string) => this.checkMaxStringLength(value));
  }

  private checkMaxStringLength(value: string, shouldTrimSpaces: boolean = false): void {
    if (!this.description) {
      return;
    }

    const cleanValue = shouldTrimSpaces ? trimSpaces(value) : value;
    if (getStringLength(trimSpaces(value)) > this.maxInputLength) {
      this.description?.setValue(trimMaxLength(trimSpaces(value), this.maxInputLength));
    } else {
      this.description?.setValue(cleanValue);
    }
  }

  private initRegisters(): void {
    if (this.registers.length === 0 && this.data.getRegistersFromApi) {
      this.getRegistries();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      registerRef: [this.newRegisterName],
      description: ['', [Validators.maxLength(30), Validators.pattern(this.pattern)]],
      date: [new Date(), Validators.required],
    });

    if (this.data.canEditDescription) {
      this.description.enable();
      this.subscribeToRegisterRefChanges();
    } else {
      this.description.disable();
    }

    if (this.data.canEditDate) {
      this.date.enable();
    } else {
      this.date.disable();
    }
  }

  private getRegistries(): void {
    this.pending = true;
    this.intRegistersApi.getRegisters(1, 10000)
      .pipe(
        finalize(() => this.pending = false),
        map((data: any) => data.list),
      ).subscribe((registers: IIntInvoiceRegister[]) => {
      this.registers = registers.filter((register) => (register as any).printed === '0');
    });
  }

  private subscribeToRegisterRefChanges(): void {
    this.subscriptions = this.registerRef?.valueChanges
      .pipe(startWith(this.newRegisterName), pipe(distinctUntilChanged()))
      .subscribe((value) => {
        if (value === this.newRegisterName) {
          if (this.data.canEditDescription) {
            this.description.enable();
            this.clearField();
          } else {
            this.description.disable();
          }

          if (this.data.canEditDate) {
            this.date.enable();
          } else {
            this.date.disable();
          }

          this.date.setValue(new Date());
        } else {
          this.clearField();
          this.description.setValue(this.registerRef.value.description);
          this.date.setValue(this.registerRef.value.dateTime);
          this.description.disable();
          this.date.disable();
        }
      });
  }

  private addToNewRegister(): void {
    const data: IIntInvoiceRegisterInsertPayload = {
      Date: moment(this.date.value).format('DD.MM.YYYY'),
      DocumentRefs: this.invoicesIds,
      Description: this.description.value,
    };

    this.intRegistersApi.insert(data)
      .pipe(finalize(() => this.pending = false))
      .subscribe(
        (response: PartialSuccessResponse) => this.handleResponse(response),
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.dialogRef.close({ register: null, type: NewInternationalRegisterMode.NEW, success: true });
        },
      );
  }

  private insertIntoExistRegister(): void {
    const data: IIntInvoiceRegisterInsertPayload = {
      Ref: this.registerRef.value.ref,
      DocumentRefs: this.invoicesIds,
    };

    this.intRegistersApi.insert(data)
      .pipe(finalize(() => (this.pending = false)))
      .subscribe(
        (response: PartialSuccessResponse) => this.handleResponse(response),
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          // Here we push NewRegisterMode.NEW to trigger reload of the registers list
          this.dialogRef.close({ register: null, type: NewInternationalRegisterMode.NEW, success: true });
        },
      );
  }

  private handleResponse(response: PartialSuccessResponse): void {
    if (!response) {
      this.dialogRef.close({ success: false, partialSuccess: false });
      return;
    }

    if (hasFailedEntities(response)) {
      if (hasSingleError(response)) {
        this.alertToastService.pushError('Помилка при додаванні');
        this.dialogRef.close({ success: false, partialSuccess: false });
        return;
      }

      const config = {
        ...RegisterInvoicesInsertPartialSuccessConfig,
        label: response.Barcode ? `№${response.Barcode}` : '',
      };
      this.partialSuccess.open(response, config);
      this.dialogRef.close({
        Barcode: response.Barcode,
        Ref: response.Ref,
        success: true,
        partialSuccess: true,
      } as INewRegisterModalClosePayload);
    } else {
      this.alertToastService.pushSuccess('Успішно додано до реєстру');
      this.closeParams = { success: true, partialSuccess: false };
      this.dialogRef.close({
        Barcode: response.Barcode,
        Ref: response.Ref,
        success: true,
        partialSuccess: false,
      } as INewRegisterModalClosePayload);
    }
  }
}
